// Push Notification Functions (OneSignal)
// Import: import { sendTradeRequestNotification, ... } from '../lib/notifications';

import { supabase } from './supabase';

const ONESIGNAL_APP_ID = process.env.REACT_APP_ONESIGNAL_APP_ID;
const ONESIGNAL_REST_API_KEY = process.env.REACT_APP_ONESIGNAL_REST_API_KEY;


// Helper: Get all device tokens for a user (including parent's devices if user is a child)
const getUserDevices = async (userId) => {
  console.log('[Notification] getUserDevices called for userId:', userId);

  // Get user's own devices
  const { data: userDevices, error } = await supabase
    .from('user_devices')
    .select('onesignal_player_id')
    .eq('user_id', userId);

  console.log('[Notification] User devices query result:', { userDevices, error });

  if (error) {
    console.error('[Notification] Error fetching devices:', error);
  }

  let allDevices = userDevices?.map(d => d.onesignal_player_id) || [];
  console.log('[Notification] User own devices:', allDevices);

  // Check if this user is a child (has linked parents)
  const { data: parentLinks } = await supabase
    .from('family_links')
    .select('parent_id')
    .eq('child_id', userId);

  // If user has parents, also get their devices
  if (parentLinks && parentLinks.length > 0) {
    for (const link of parentLinks) {
      const { data: parentDevices } = await supabase
        .from('user_devices')
        .select('onesignal_player_id')
        .eq('user_id', link.parent_id);

      if (parentDevices) {
        const parentPlayerIds = parentDevices.map(d => d.onesignal_player_id);
        allDevices = [...allDevices, ...parentPlayerIds];
      }
    }
  }

  // Remove duplicates
  return [...new Set(allDevices)];
};

// Helper: Send push via OneSignal REST API
const sendPush = async (playerIds, title, message, data = {}) => {
  if (!playerIds.length) {
    console.log('[Notification] No devices to send to');
    return { success: false, reason: 'no_devices' };
  }

  if (!ONESIGNAL_APP_ID || !ONESIGNAL_REST_API_KEY) {
    console.log('[Notification] OneSignal not configured');
    return { success: false, reason: 'not_configured' };
  }

  try {
    const response = await fetch('https://onesignal.com/api/v1/notifications', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Basic ${ONESIGNAL_REST_API_KEY}`,
      },
      body: JSON.stringify({
        app_id: ONESIGNAL_APP_ID,
        include_player_ids: playerIds,
        headings: { en: title },
        contents: { en: message },
        data: data,
      }),
    });

    const result = await response.json();
    console.log('[Notification] Sent:', result);

    // Clean up invalid player IDs from database (stale tokens)
    if (result.errors?.invalid_player_ids?.length > 0) {
      console.log('[Notification] Cleaning up invalid player IDs:', result.errors.invalid_player_ids);
      for (const invalidId of result.errors.invalid_player_ids) {
        await supabase
          .from('user_devices')
          .delete()
          .eq('onesignal_player_id', invalidId);
      }
      console.log('[Notification] Removed', result.errors.invalid_player_ids.length, 'stale devices');
    }

    return { success: true, result };
  } catch (err) {
    console.error('[Notification] Send error:', err);
    return { success: false, error: err };
  }
};

// ============================================
// SECTION 1: Trade Requests
// ============================================

// 1.1 Incoming Trade Request
// Trigger: Home.js - when user sends a trade request
export const sendTradeRequestNotification = async (cardOwnerId, requesterName, cardName) => {
  console.log('[Notification] sendTradeRequestNotification called:', { cardOwnerId, requesterName, cardName });
  const devices = await getUserDevices(cardOwnerId);
  console.log('[Notification] Found devices for user:', devices);
  return sendPush(
    devices,
    'New Trade Request!',
    `${requesterName} wants to trade for your ${cardName}`,
    {
      type: 'trade_request',
      route: '/requests',
      tab: 'incoming'
    }
  );
};

// 1.1b Multi-Card Trade Request
// Trigger: Home.js - when user sends trade requests for multiple cards at once
export const sendMultiTradeRequestNotification = async (cardOwnerId, requesterName, cardNames) => {
  console.log('[Notification] sendMultiTradeRequestNotification called:', { cardOwnerId, requesterName, cardNames });
  const devices = await getUserDevices(cardOwnerId);
  console.log('[Notification] Found devices for user:', devices);

  const count = cardNames.length;
  let message;
  if (count === 1) {
    message = `${requesterName} wants to trade for your ${cardNames[0]}`;
  } else if (count === 2) {
    message = `${requesterName} wants to trade for your ${cardNames[0]} and ${cardNames[1]}`;
  } else {
    message = `${requesterName} wants to trade for ${count} of your cards: ${cardNames.slice(0, 2).join(', ')} and ${count - 2} more`;
  }

  return sendPush(
    devices,
    count === 1 ? 'New Trade Request!' : `${count} New Trade Requests!`,
    message,
    {
      type: 'trade_request',
      route: '/requests',
      tab: 'incoming'
    }
  );
};

// 1.2 Trade Request Accepted
// Trigger: Requests.js - when owner accepts a request
export const sendTradeAcceptedNotification = async (requesterId, ownerName, cardName, tradeId) => {
  console.log('[Notification] sendTradeAcceptedNotification called:', { requesterId, ownerName, cardName, tradeId });
  const devices = await getUserDevices(requesterId);
  console.log('[Notification] Found devices for requester:', devices);
  return sendPush(
    devices,
    'Trade Request Accepted!',
    `${ownerName} accepted your request for ${cardName}`,
    {
      type: 'trade_accepted',
      route: '/chat',
      tradeId: tradeId
    }
  );
};

// ============================================
// SECTION 2: Chat Messages
// ============================================

// 2.1 New Message Received
// Trigger: Chat.js - when message is sent
export const sendNewMessageNotification = async (recipientId, senderName, messagePreview, tradeId) => {
  const devices = await getUserDevices(recipientId);
  const preview = messagePreview.length > 50 ? messagePreview.slice(0, 50) + '...' : messagePreview;
  return sendPush(
    devices,
    senderName,
    preview,
    {
      type: 'new_message',
      route: '/chat',
      tradeId: tradeId
    }
  );
};

// ============================================
// SECTION 3: Post Interactions
// ============================================

// 3.1 Reaction on Post
// Trigger: Home.js - when user taps a reaction
export const sendReactionNotification = async (postOwnerId, reactorName, cardName, cardId, reactionType) => {
  const devices = await getUserDevices(postOwnerId);

  // Format ball name for title
  const ballNames = {
    poke: 'Poke Ball',
    great: 'Great Ball',
    ultra: 'Ultra Ball',
    master: 'Master Ball'
  };
  const ballName = ballNames[reactionType] || 'Poke Ball';

  return sendPush(
    devices,
    ballName,
    `${reactorName} liked your ${cardName} post`,
    {
      type: 'post_reaction',
      route: '/card',
      cardId: cardId,
      reactorName: reactorName,
      reactionType: reactionType
    }
  );
};

// 3.2 Followed User Posted
// Trigger: Post flow - after successful post
export const sendFollowedUserPostedNotification = async (followerId, posterName, cardName, cardId, postId) => {
  console.log('[Notification] sendFollowedUserPostedNotification:', { followerId, posterName, cardName, cardId, postId });
  const devices = await getUserDevices(followerId);
  console.log('[Notification] Devices for follower', followerId, ':', devices);
  const result = await sendPush(
    devices,
    'New Post!',
    `${posterName} just posted ${cardName}`,
    {
      type: 'followed_post',
      route: '/home',
      cardId: cardId,
      postId: postId
    }
  );
  console.log('[Notification] Push result for', followerId, ':', result);
  return result;
};

// 3.3 New Follower
// Trigger: Home.js or ManageFollowing.js - when follow action
export const sendNewFollowerNotification = async (followedUserId, followerName, followerId) => {
  const devices = await getUserDevices(followedUserId);
  return sendPush(
    devices,
    'New Follower!',
    `${followerName} started following you`,
    {
      type: 'new_follower',
      route: '/user',
      userId: followerId,
      followerName: followerName
    }
  );
};

// ============================================
// SECTION 4: Trade Scheduling & Reminders
// ============================================

// 4.1 Trade Scheduled
// Trigger: Chat.js - when schedule is confirmed
export const sendTradeScheduledNotification = async (userId, partnerName, locationName, dateTime, tradeId) => {
  const devices = await getUserDevices(userId);
  return sendPush(
    devices,
    'Trade Meetup Scheduled!',
    `Meet ${partnerName} at ${locationName} on ${dateTime}`,
    {
      type: 'trade_scheduled',
      route: '/trade',
      tradeId: tradeId
    }
  );
};

// 4.1b Trade Scheduled - Notify Location Staff
// Trigger: Chat.js - when schedule is confirmed at a location
export const sendLocationTradeNotification = async (locationId, trader1Name, trader2Name, dateTime) => {
  console.log('[Notification] sendLocationTradeNotification called:', { locationId, trader1Name, trader2Name, dateTime });

  // Get all active staff for this location
  const { data: staffMembers, error } = await supabase
    .from('location_staff')
    .select('user_id')
    .eq('location_id', locationId)
    .eq('status', 'active');

  if (error || !staffMembers?.length) {
    console.log('[Notification] No staff found for location:', locationId);
    return { success: false, reason: 'no_staff' };
  }

  console.log('[Notification] Found', staffMembers.length, 'staff members to notify');

  // Collect all devices for all staff members
  let allDevices = [];
  for (const staff of staffMembers) {
    const devices = await getUserDevices(staff.user_id);
    allDevices = [...allDevices, ...devices];
  }

  // Remove duplicates
  allDevices = [...new Set(allDevices)];

  if (allDevices.length === 0) {
    console.log('[Notification] No devices found for staff members');
    return { success: false, reason: 'no_devices' };
  }

  return sendPush(
    allDevices,
    'Trade Scheduled at Your Store!',
    `${trader1Name} & ${trader2Name} will meet on ${dateTime}`,
    {
      type: 'location_trade_scheduled',
      route: '/location-management',
    }
  );
};

// 4.2 Trade Reminder - Day Before (Edge Function)
// 4.3 Trade Reminder - Day Of (Edge Function)
// 4.4 Trade Review Prompt (Edge Function)
// These are triggered by Supabase cron, not in-app

// ============================================
// SECTION 5: Daily Summary (Edge Function)
// ============================================

// Morning digest is handled by edge function

// ============================================
// SECTION 6: Security & Account
// ============================================

// 6.1 New Device Login
// Trigger: Auth flow - on new device detected
export const sendNewDeviceAlertNotification = async (userId, deviceInfo) => {
  const devices = await getUserDevices(userId);
  return sendPush(
    devices,
    'Security Alert',
    `New login from ${deviceInfo}`,
    {
      type: 'security_alert',
      alertType: 'new_device',
      route: '/profile'
    }
  );
};

// 6.2 Password Changed
// Trigger: Profile settings - password change
export const sendPasswordChangedNotification = async (userId) => {
  const devices = await getUserDevices(userId);
  return sendPush(
    devices,
    'Security Alert',
    'Your password was changed',
    {
      type: 'security_alert',
      alertType: 'password_changed',
      route: '/profile'
    }
  );
};

// ============================================
// SECTION 7: Milestones & Achievements
// ============================================

// 7.1 Trade Milestone
// Trigger: Trade completion flow
export const sendMilestoneNotification = async (userId, milestoneCount) => {
  const devices = await getUserDevices(userId);
  return sendPush(
    devices,
    'Milestone Reached!',
    `Congrats! You completed your ${milestoneCount}th trade!`,
    {
      type: 'milestone',
      route: '/profile',
      milestone: milestoneCount
    }
  );
};

// 7.2 Rating Warning
// Trigger: Review submission flow
export const sendRatingWarningNotification = async (userId, newRating) => {
  const devices = await getUserDevices(userId);
  return sendPush(
    devices,
    'Rating Update',
    'Your trader rating has changed. Check your recent feedback.',
    {
      type: 'rating_warning',
      route: '/profile',
      newRating: newRating
    }
  );
};

// ============================================
// SECTION 8: Trade Cancellation & Reschedule
// ============================================

// 8.1 Trade Cancelled
export const sendTradeCancelledNotification = async (userId, cancellerName, locationName, tradeId) => {
  const devices = await getUserDevices(userId);
  if (!devices.length) return;
  return sendPush(devices, 'Trade Cancelled',
    `@${cancellerName} cancelled the trade at ${locationName}`,
    { type: 'trade_cancelled', route: '/chat', tradeId });
};

// 8.2 Running Late
export const sendRunningLateNotification = async (userId, partnerName, minutes, tradeId) => {
  const devices = await getUserDevices(userId);
  if (!devices.length) return;
  return sendPush(devices, 'Running Late',
    `@${partnerName} is running ${minutes} minutes late. Open the chat to respond.`,
    { type: 'running_late', route: '/chat', tradeId });
};

// 8.3 Reschedule Request
export const sendRescheduleRequestNotification = async (userId, partnerName, newDateTime, tradeId) => {
  const devices = await getUserDevices(userId);
  if (!devices.length) return;
  return sendPush(devices, 'Reschedule Request',
    `@${partnerName} wants to reschedule to ${newDateTime}. Open the chat to respond.`,
    { type: 'reschedule_request', route: '/chat', tradeId });
};

// 8.4 Reschedule Response
export const sendRescheduleResponseNotification = async (userId, partnerName, accepted, tradeId) => {
  const devices = await getUserDevices(userId);
  if (!devices.length) return;
  const title = accepted ? 'Reschedule Accepted' : 'Reschedule Declined';
  const msg = accepted
    ? `@${partnerName} accepted the new time.`
    : `@${partnerName} declined the reschedule. The trade has been cancelled.`;
  return sendPush(devices, title, msg,
    { type: 'reschedule_response', route: '/chat', tradeId });
};

// 8.5 Late Response
export const sendLateResponseNotification = async (userId, partnerName, accepted, tradeId) => {
  const devices = await getUserDevices(userId);
  if (!devices.length) return;
  const title = accepted ? 'Delay Accepted' : 'Trade Cancelled';
  const msg = accepted
    ? `@${partnerName} is OK with the delay.`
    : `@${partnerName} cancelled due to the delay.`;
  return sendPush(devices, title, msg,
    { type: 'late_response', route: '/chat', tradeId });
};

// 8.6 Card removed from trade
export const sendCardRemovedFromTradeNotification = async (userId, ownerName, cardName, tradeId) => {
  const devices = await getUserDevices(userId);
  if (!devices.length) return;
  return sendPush(devices, 'Card Removed',
    `@${ownerName} removed "${cardName}" from the trade.`,
    { type: 'card_removed', route: '/chat', tradeId });
};

// 8.7 Reschedule/Late Expired (no response)
export const sendRescheduleExpiredNotification = async (userId, partnerName, tradeId) => {
  const devices = await getUserDevices(userId);
  if (!devices.length) return;
  return sendPush(devices, 'Trade Cancelled',
    `@${partnerName} didn't respond to your request. The trade has been cancelled.`,
    { type: 'reschedule_expired', route: '/chat', tradeId });
};

// ============================================
// UTILITY: Send to multiple followers
// ============================================

// Bulk send to all followers (for "followed user posted")
export const notifyFollowers = async (posterId, cardName, cardId, postId) => {
  console.log('[Notification] notifyFollowers called:', { posterId, cardName, cardId, postId });

  // Get all users who follow this poster
  const { data: followers, error } = await supabase
    .from('user_follows')
    .select('follower_id')
    .eq('following_id', posterId);

  console.log('[Notification] Followers query result:', { followers, error });

  if (error || !followers?.length) {
    console.log('[Notification] No followers to notify');
    return;
  }

  console.log('[Notification] Found', followers.length, 'followers to notify');

  // Get poster name
  const { data: poster } = await supabase
    .from('users')
    .select('username')
    .eq('id', posterId)
    .single();

  const posterName = poster?.username || 'Someone';
  console.log('[Notification] Poster name:', posterName);

  // Send to each follower
  console.log('[Notification] Sending to followers:', followers.map(f => f.follower_id));
  const results = await Promise.all(
    followers.map(f => sendFollowedUserPostedNotification(f.follower_id, posterName, cardName, cardId, postId))
  );

  console.log('[Notification] Send results:', results);
  return results;
};
